import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { GIFT_VARIANT_ID, readSampleCart, rewardKitPlan } from '@theme/haus-sample-promotion';

const SOURCE_ID = 'haus-sample-reward-guard';

class HausSampleRewardGuard {
  constructor() {
    this.pending = false;
    this.inFlight = null;
    this.abortController = new AbortController();
    this.checkoutAllowed = false;
    this.cartUpdates = new Set();
    this.quantityIntent = false;
    this.nativeFailure = false;
    this.nativeNavigation = false;
    this.nativeSnapshot = null;
  }

  connect() {
    const {signal} = this.abortController;
    document.addEventListener(ThemeEvents.cartUpdate, event => {
      if (event?.detail?.sourceId !== SOURCE_ID && event?.detail?.data?.source !== SOURCE_ID) this.scheduleReconcile();
    }, {signal});
    document.addEventListener(ThemeEvents.discountUpdate, () => this.scheduleReconcile(), {signal});
    window.addEventListener('pageshow', () => this.scheduleReconcile(), {signal});
    document.addEventListener('click', event => this.checkCheckout(event), {capture: true, signal});
    document.addEventListener('submit', event => this.checkCheckout(event), {capture: true, signal});
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, event => {
      if (!event.target?.closest?.('cart-items-component')) return;
      this.quantityIntent = true;
      this.setNativeReady(false);
    }, {capture: true, signal});
    document.addEventListener('click', event => {
      if (!event.target?.closest?.('.haus-benefits__apply')) return;
      this.nativeNavigation = true;
      this.setNativeReady(false);
    }, {capture: true, signal});
    document.addEventListener('visibilitychange', () => {
      this.setNativeReady(false);
      if (!document.hidden) this.scheduleReconcile();
    }, {signal});
    window.addEventListener('pagehide', () => this.setNativeReady(false), {signal});
    window.addEventListener('pageshow', () => { this.nativeNavigation = false; }, {signal});
    this.scheduleReconcile();
  }

  setNativeReady(ready) {
    for (const element of document.querySelectorAll('[data-haus-native-cart]')) {
      element.hidden = !ready;
      element.inert = !ready;
      element.dataset.state = ready ? 'ready' : 'pending';
    }
  }

  beginCartUpdate(kind) {
    if (!this.cartUpdates.size) this.nativeFailure = false;
    const token = Symbol(kind);
    this.cartUpdates.add(token);
    if (kind === 'quantity') this.quantityIntent = false;
    this.setNativeReady(false);
    return success => {
      this.cartUpdates.delete(token);
      if (!success) this.nativeFailure = true;
      this.scheduleReconcile();
    };
  }

  scheduleReconcile() {
    this.setNativeReady(false);
    window.clearTimeout(this.reconcileTimer);
    this.reconcileTimer = window.setTimeout(() => this.reconcile().catch(error => this.showError(error)), 120);
  }

  reconcile() {
    window.clearTimeout(this.reconcileTimer);
    this.pending = true;
    this.setNativeReady(false);
    if (!this.inFlight) this.inFlight = this.run().then(snapshot => {
      this.nativeSnapshot = snapshot;
      return snapshot;
    }).catch(error => {
      this.nativeSnapshot = null;
      throw error;
    }).finally(() => {
      this.inFlight = null;
      this.setNativeReady(Boolean(this.nativeSnapshot?.cart?.item_count) && !this.pending &&
        !this.cartUpdates.size && !this.quantityIntent && !this.nativeFailure &&
        !this.nativeNavigation && !document.hidden);
    });
    return this.inFlight;
  }

  async run() {
    let snapshot;
    let passes = 0;
    do {
      if (++passes > 8) throw new Error('Your cart changed while checking rewards. Please try checkout again.');
      this.pending = false;
      snapshot = await readSampleCart();
      const plan = rewardKitPlan(snapshot.cart, snapshot.promotion);
      if (Object.keys(plan.updates).length || plan.add) {
        const root = window.Shopify?.routes?.root || '/';
        const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(component => component.dataset.sectionId).filter(Boolean))].slice(0, 5);
        const body = {
          ...(plan.add ? {items: [{id: Number(GIFT_VARIANT_ID), quantity: 1, properties: {_haus_sample_reward: '15-sample grout kit'}}]} : {updates: plan.updates}),
          sections_url: window.location.pathname,
          ...(sections.length ? {sections: sections.join(',')} : {}),
        };
        const response = await fetch(`${root}cart/${plan.add ? 'add' : 'update'}.js`, {method: 'POST', headers: {Accept: 'application/json', 'Content-Type': 'application/json'}, body: JSON.stringify(body)});
        if (response.status === 429) {
          const delay = Math.min(5000, Math.max(1000, Number(response.headers.get('Retry-After') || 1) * 1000));
          await new Promise(resolve => window.setTimeout(resolve, delay));
          this.pending = true;
          continue;
        }
        const result = await response.json();
        if (!response.ok || result.errors || result.status) throw new Error(result.description || 'Your free grout sampler could not be updated. Refresh your cart before checkout.');
        const latest = await readSampleCart();
        document.dispatchEvent(new CartUpdateEvent(latest.cart, SOURCE_ID, {source: SOURCE_ID, itemCount: latest.cart.item_count, sections: result.sections}));
        this.pending = true;
      }
    } while (this.pending);
    document.getElementById('HausSampleRewardError')?.remove();
    return snapshot;
  }

  checkCheckout(event) {
    if (this.checkoutAllowed || event.defaultPrevented) return;
    const target = event.target instanceof Element ? event.target : null;
    const control = event.type === 'submit' ? event.submitter || target?.querySelector('[name="checkout"]') : target?.closest('button[name="checkout"], input[name="checkout"], a[href]');
    if (!control) return;
    const href = control instanceof HTMLAnchorElement ? new URL(control.href, window.location.href) : null;
    if (href ? href.origin !== window.location.origin || !/^\/checkout(?:\/|$)/.test(href.pathname) : control.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.checkoutPending) return;
    this.checkoutPending = true;
    this.reconcile().then(() => {
      this.checkoutAllowed = true;
      try {
        if (event.type === 'submit') event.target.requestSubmit(control);
        else control.click();
      } finally {this.checkoutAllowed = false;}
    }).catch(error => this.showError(error)).finally(() => {this.checkoutPending = false;});
  }

  showError(error) {
    this.setNativeReady(false);
    let element = document.getElementById('HausSampleRewardError');
    if (!element) {
      element = document.createElement('p');
      element.id = 'HausSampleRewardError';
      element.setAttribute('role', 'alert');
      const checkout = document.querySelector('button[name="checkout"], input[name="checkout"]');
      if (checkout?.parentElement) checkout.parentElement.prepend(element);
      else document.querySelector('haus-sample-builder [data-builder-status]')?.after(element);
    }
    element.textContent = error.message || 'Your sample reward could not be checked. Refresh your cart before checkout.';
  }
}

if (!window.HausSampleRewardGuard) {
  window.HausSampleRewardGuard = new HausSampleRewardGuard();
  window.HausSampleRewardGuard.connect();
}


if (!customElements.get('haus-cart-payments')) {
  customElements.define('haus-cart-payments', class extends HTMLElement {
    connectedCallback() {
      this.hidden = true;
      this.inert = true;
      window.HausSampleRewardGuard?.scheduleReconcile();
    }
  });
}
